import React, { FC, useEffect, useState } from "react";
import {
  TechnologyCharge,
  LibraryTechnology,
  TechnologyUnit,
  TechnologyChargeType,
  formatPrice,
} from "../../data-types";
import { Select, Radio, Button, InputNumber, Form, Collapse, Input } from "antd";
import { Icons } from "../vpd-icons/icons";
import { PriceInput, PercentageInput } from "../input-number";

const { Panel } = Collapse;

import styles from "./technology-charge-row.module.css";

const {
  ["technology-charge-container"]: technologyChargeContainerClassName,
  ["description-input"]: descriptionInputClassName,
  ["type-radio"]: typeRadioClassName,
  ["radio-buttons"]: radioButtonClassname,
  ["top-row"]: topRowClassName,
  ["charges-row"]: chargesRowClassName,
  ["base-plus-units"]: basePlusUnitsClassName,
  ["units-row"]: unitsRowClassName,
  ["total-row"]: totalRowClassName,
  ["calculation-item"]: calculationItemClassName,
} = styles as any;

type Props = {
  technologyCharge: TechnologyCharge;
  libraryTechnologies: LibraryTechnology[];
  technologyUnits: TechnologyUnit[];
  onUpdate: (technologyCharge: Partial<TechnologyCharge>) => void;
  onDelete: (id: number) => void;
  autoFocus?: boolean;
  onAutoFocus?: () => void;
  currencySymbol: string;
};

export const TechnologyChargeRow: FC<Props> = ({
  technologyCharge,
  libraryTechnologies,
  technologyUnits,
  onUpdate,
  onDelete,
  autoFocus,
  onAutoFocus,
  currencySymbol,
}) => {
  const {
    libraryTechnologyId: initialLibraryTechnologyId,
    type: initialType,
    fixedAmount: initialFixedAmount,
    baseAmount: initialBaseAmount,
    percentage: initialPercentage,
    technologyUnitId: initialTechnologyUnitId,
    unitCost: initialUnitCost,
    units: initialUnits,
    notes: initialNotes,
  } = technologyCharge;

  const [libraryTechnologyId, setLibraryTechnologyId] = useState<number | null>(
    initialLibraryTechnologyId ?? null
  );
  const [type, setType] = useState(initialType);
  const [fixedAmount, setFixedAmount] = useState<number | null>(initialFixedAmount ?? null);
  const [baseAmount, setBaseAmount] = useState<number | null>(initialBaseAmount ?? null);
  const [percentage, setPercentage] = useState<number | null>(initialPercentage ?? null);
  const [technologyUnitId, setTechnologyUnitId] = useState<number | null>(
    initialTechnologyUnitId ?? null
  );
  const [unitCost, setUnitCost] = useState<number | null>(initialUnitCost ?? null);
  const [units, setUnits] = useState<number | null>(initialUnits ?? null);
  const [notes, setNotes] = useState<string>(initialNotes ?? "");

  useEffect(() => {
    autoFocus && onAutoFocus && onAutoFocus();
  }, [autoFocus, onAutoFocus]);

  const technologyOptions = libraryTechnologies.map((lt) => ({
    value: lt.id,
    label: lt.description,
  }));

  const renderChargeFields = () => {
    switch (type) {
      case TechnologyChargeType.Fixed:
        return (
          <Form.Item label="Fixed amount">
            <PriceInput
              currencySymbol={currencySymbol}
              placeholder="Fixed amount"
              value={fixedAmount}
              onChange={(value) => {
                setFixedAmount(value ?? 0);
                onUpdate({ ...technologyCharge, fixedAmount: value });
              }}
            />
          </Form.Item>
        );

      case TechnologyChargeType.BasePlusUnits:
        const subtotal = ((unitCost || 0) * (units || 0)).toFixed(2);
        const total = ((baseAmount || 0) + Number(subtotal)).toFixed(2);

        return (
          <div className={basePlusUnitsClassName}>
            <Form.Item label="Base charge">
              <PriceInput
                currencySymbol={currencySymbol}
                placeholder="Base charge"
                value={baseAmount}
                onChange={(value) => {
                  setBaseAmount(value ?? 0);
                  onUpdate({ ...technologyCharge, baseAmount: value });
                }}
              />
            </Form.Item>
            <div className={unitsRowClassName}>
              <Form.Item label="Unit">
                <Select
                  style={{ width: "150px" }}
                  options={technologyUnits.map((tu) => ({
                    value: tu.id,
                    label: tu.name,
                  }))}
                  placeholder="Unit"
                  value={technologyUnitId}
                  onChange={(value) => {
                    setTechnologyUnitId(value);
                    onUpdate({
                      ...technologyCharge,
                      technologyUnitId: value,
                    });
                  }}
                />
              </Form.Item>
              <Form.Item label="Unit cost">
                <PriceInput
                  currencySymbol={currencySymbol}
                  placeholder="Unit cost"
                  value={unitCost}
                  onChange={(value) => {
                    setUnitCost(value ?? 0);
                    onUpdate({ ...technologyCharge, unitCost: value ?? 0 });
                  }}
                />
              </Form.Item>
              <Form.Item label="No. of units">
                <InputNumber
                  placeholder="No. of units"
                  value={units}
                  formatter={(value) => (value != null ? Number(value).toLocaleString() : "")}
                  parser={(value: string | undefined) =>
                    value ? Number(value.replace(/,/g, "")) : 0
                  }
                  onChange={(value) => {
                    setUnits(value ?? 0);
                    onUpdate({ ...technologyCharge, units: value ?? 0 });
                  }}
                />
              </Form.Item>
              <div className={calculationItemClassName}>
                Subtotal: {formatPrice(Number(subtotal), currencySymbol)}
              </div>
            </div>
            <div className={totalRowClassName}>
              <div className={calculationItemClassName}>
                Total: {formatPrice(Number(total), currencySymbol)}
              </div>
            </div>
          </div>
        );

      case TechnologyChargeType.Percentage:
        return (
          <>
            <Form.Item label="Percentage">
              <PercentageInput
                placeholder="Percentage"
                value={percentage}
                onChange={(value) => {
                  setPercentage(value ?? 0);
                  onUpdate({ ...technologyCharge, percentage: value });
                }}
              />
            </Form.Item>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <div className={technologyChargeContainerClassName}>
      <div className={styles["content-wrapper"]}>
        <div className={topRowClassName}>
          <div className={descriptionInputClassName}>
            <Select
              autoFocus={autoFocus}
              options={technologyOptions}
              placeholder="Technology description"
              defaultValue={
                technologyOptions.find((option) => option.value === libraryTechnologyId)?.value
              }
              showSearch
              filterOption={(inputValue, option) =>
                option!.label.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1
              }
              onChange={(value) => {
                setLibraryTechnologyId(value);
                const libraryTechnology = libraryTechnologies.find((lt) => lt.id === value);
                setTechnologyUnitId(libraryTechnology?.technologyUnitId ?? null);

                // Get unit cost from matching rate based on currency
                const matchingRate = libraryTechnology?.technologyRates?.find(
                  (rate) => rate.currencyId === technologyCharge.currencyId
                );
                const unitCostValue = matchingRate?.unitCost ?? 0;
                setUnitCost(unitCostValue);

                onUpdate({
                  ...technologyCharge,
                  libraryTechnologyId: value,
                  description: libraryTechnology?.description,
                  technologyUnitId: libraryTechnology?.technologyUnitId,
                  unitCost: unitCostValue,
                });
              }}
            />
          </div>
          <div className={typeRadioClassName}>
            <Radio.Group
              defaultValue={technologyCharge.type}
              buttonStyle="solid"
              onChange={(e) => {
                setType(e.target.value);
                onUpdate({
                  ...technologyCharge,
                  type: e.target.value,
                });
              }}
              className={radioButtonClassname}
            >
              <Radio.Button value={TechnologyChargeType.Fixed}>Fixed</Radio.Button>
              <Radio.Button value={TechnologyChargeType.BasePlusUnits}>Base + Units</Radio.Button>
              <Radio.Button value={TechnologyChargeType.Percentage}>Percentage</Radio.Button>
            </Radio.Group>
          </div>
        </div>
        <div className={chargesRowClassName}>{renderChargeFields()}</div>
        <div className={chargesRowClassName}>
          <Collapse style={{ width: "100%" }} ghost defaultActiveKey={notes ? ["1"] : undefined}>
            <Panel header="Notes" key="1">
              <Input
                placeholder="Explanatory notes"
                value={notes}
                onChange={(e) => {
                  setNotes(e.target.value);
                  onUpdate({ ...technologyCharge, notes: e.target.value });
                }}
              />
            </Panel>
          </Collapse>
        </div>
      </div>
      <Button
        icon={Icons.Delete}
        shape="circle"
        onClick={() => onDelete(technologyCharge.id)}
        type="link"
      />
    </div>
  );
};
